import { getApiClient } from "@punchup/utils/apiRegistry";
import type {
  SupportCaseDraftDTO,
  SupportCaseDTO,
  SupportCaseListResponse,
  SupportCaseStatus,
  SupportMediaUploadTarget,
} from "../../models/support";

/**
 * Browser-side calls to /api/v2/internal/support. Request bodies are typed by
 * the callers (the @punchup/support hooks, against its zod schemas) — this
 * layer only moves them.
 */

const BASE = "/api/v2/internal/support";

export type SupportCaseListParams = {
  q?: string;
  category?: string;
  status?: SupportCaseStatus;
  cursor?: string;
  limit?: number;
};

export async function fetchSupportCases(
  params: SupportCaseListParams = {}
): Promise<SupportCaseListResponse> {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== "") search.set(key, String(value));
  }
  const query = search.toString();
  const response = await getApiClient().get<{ data: SupportCaseListResponse }>(
    `${BASE}/cases${query ? `?${query}` : ""}`
  );
  return response.data;
}

export async function fetchSupportCase(
  caseId: string
): Promise<SupportCaseDTO> {
  const response = await getApiClient().get<{ data: SupportCaseDTO }>(
    `${BASE}/cases/${encodeURIComponent(caseId)}`
  );
  return response.data;
}

export async function postSupportCase(body: object): Promise<SupportCaseDTO> {
  const response = await getApiClient().post<{ data: SupportCaseDTO }>(
    `${BASE}/cases`,
    body
  );
  return response.data;
}

export async function patchSupportCase(
  caseId: string,
  body: object
): Promise<SupportCaseDTO> {
  const response = await getApiClient().patch<{ data: SupportCaseDTO }>(
    `${BASE}/cases/${encodeURIComponent(caseId)}`,
    body
  );
  return response.data;
}

export async function postSupportCaseVerification(
  caseId: string,
  action: "verify" | "unverify"
): Promise<SupportCaseDTO> {
  const response = await getApiClient().post<{ data: SupportCaseDTO }>(
    `${BASE}/cases/${encodeURIComponent(caseId)}/verify`,
    { action }
  );
  return response.data;
}

export async function requestSupportMediaUpload(body: {
  kind: "image" | "video";
  mimeType: string;
  sizeBytes: number;
}): Promise<SupportMediaUploadTarget> {
  const response = await getApiClient().post<{
    data: SupportMediaUploadTarget;
  }>(`${BASE}/media/upload-url`, body);
  return response.data;
}

/** Claude's draft of a case from a conversation; nothing is saved. */
export async function requestSupportCaseDraft(
  conversationId: string
): Promise<SupportCaseDraftDTO> {
  const response = await getApiClient().post<{ data: SupportCaseDraftDTO }>(
    `${BASE}/conversations/${encodeURIComponent(conversationId)}/case-draft`,
    {}
  );
  return response.data;
}

/** A draft description for an uploaded screenshot. */
export async function requestSupportMediaDescription(
  storagePath: string
): Promise<string> {
  const response = await getApiClient().post<{
    data: { description: string };
  }>(`${BASE}/media/describe`, { storagePath });
  return response.data.description;
}
